import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
} from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../store";
import { appActions } from "../../../../store/slices/appSlice";
import { useFocusEffect } from "@react-navigation/native";
import { MaterialIcons, Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import { LinearGradient } from "expo-linear-gradient";
import i18n from "../../../../locales/i18n";
import StandardNavigation from "../../../../components/navigation/StandardNavigation";
import customAxios from "../../../../utils/customAxios";
import { Toast } from "react-native-toast-notifications";
import Loader from "../../../../components/loader/Loader";
import WebLimitsScreen from "../../../../components/web/WebLimitsScreen";
import Animated, { FadeInDown } from "react-native-reanimated";

interface UserLimits {
  error?: string;
  productCount?: number;
  debtCount?: number;
  factureCount?: number;
}

interface LimitItemProps {
  icon: string;
  title: string;
  used: number;
  limit: number;
  color: string;
  locale: string;
}

const LimitItem: React.FC<LimitItemProps> = ({
  icon,
  title,
  used,
  limit,
  color,
  locale,
}) => {
  const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const remaining = limit > 0 ? Math.max(limit - used, 0) : 0;

  const getStatusColor = () => {
    if (percentage >= 100) return "#EF4444"; // Red - only when actually exceeded
    if (percentage >= 90) return "#F59E0B"; // Orange - approaching limit
    if (percentage >= 70) return "#F59E0B"; // Orange
    return "#10B981"; // Green
  };

  const getStatusText = () => {
    if (percentage >= 100) return i18n.t("freeTierLimitExceeded");
    if (percentage >= 90) return i18n.t("approachingLimit"); // New message for approaching
    if (percentage >= 70) return i18n.t("nearLimit");
    return i18n.t("withinLimits");
  };

  return (
    <Animated.View 
      entering={FadeInDown.duration(400).delay(200)}
      className="bg-white rounded-xl p-5 mb-4 shadow-sm border border-gray-100"
    >
      <View
        className={`flex-row items-center justify-between ${locale === "ar" ? "flex-row-reverse" : "flex-row"}`}
      >
        <View
          className={`flex-row items-center ${locale === "ar" ? "flex-row-reverse" : "flex-row"}`}
        >
          <View
            className={`w-10 h-10 rounded-full items-center justify-center`}
            style={{ backgroundColor: color + "20" }}
          >
            <MaterialIcons name={icon as any} size={20} color={color} />
          </View>
          <Text
            className={`font-semibold text-lg text-gray-800 ${locale === "ar" ? "mr-3" : "ml-3"}`}
          >
            {title}
          </Text>
        </View>

        <View
          className={`items-end ${locale === "ar" ? "items-start" : "items-end"}`}
        >
          <Text className="text-sm text-gray-500">
            {used} / {limit === 0 ? i18n.t("unlimited") : limit}
          </Text>
          <Text
            className="text-xs font-medium"
            style={{ color: getStatusColor() }}
          >
            {percentage.toFixed(0)}%
          </Text>
        </View>
      </View>

      {/* Progress Bar */}
      <View className="mt-4">
        <View className="bg-gray-200 rounded-full h-2">
          <View
            className="h-2 rounded-full transition-all duration-300"
            style={{
              width: `${percentage}%`,
              backgroundColor: getStatusColor(),
            }}
          />
        </View>

        <View
          className={`flex-row justify-between mt-2 ${locale === "ar" ? "flex-row-reverse" : "flex-row"}`}
        >
          <Text className="text-sm text-gray-600">
            {i18n.t("used")}: {used}
          </Text>
          <Text className="text-sm text-gray-600">
            {i18n.t("remainingLimit")}:{" "}
            {remaining === 0 && limit > 0 ? 0 : remaining}
          </Text>
        </View>
      </View>

      {percentage >= 90 && (
        <View
          className={`mt-3 p-3 rounded-lg ${
            percentage >= 100
              ? "bg-red-50 border border-red-200"
              : "bg-orange-50 border border-orange-200"
          }`}
        >
          <Text
            className={`text-sm font-medium text-center ${
              percentage >= 100 ? "text-red-700" : "text-orange-700"
            }`}
          >
            {getStatusText()}
          </Text>
        </View>
      )}
    </Animated.View>
  );
};

const LimitsPage = () => {
  const dispatch = useDispatch();
  const { locale } = useSelector((state: RootState) => state.app);
  const { user } = useSelector((state: RootState) => state.auth);
  const [limits, setLimits] = useState<UserLimits | null>(null);
  const [loading, setLoading] = useState(true);

  // Update locale when screen is focused
  useFocusEffect(
    React.useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch])
  );

  // Fetch user limits
  const fetchUserLimits = async () => {
    try {
      setLoading(true);
      const response = await customAxios.get("/users/limite", {
        withCredentials: true,
      });

      // The response has a 'limite' object inside response.data
      const limitsData = response.data?.limite || response.data;
      setLimits(limitsData);
    } catch (error: any) {
      // Handle the case when user exceeds free tier limits (403 response)
      if (error?.response?.status === 403) {
        // Check if limits data exists in the error response
        const limitsData = error.response.data?.limite || error.response.data;

        if (limitsData) {
          setLimits({
            productCount: limitsData.productCount || 0,
            debtCount: limitsData.debtCount || 0,
            factureCount: limitsData.factureCount || 0,
            error: error.response.data?.error,
          });
        }
      } else {
        // Only log errors that are NOT 403 (free tier limits)
        Toast.show(i18n.t("errorOccurred"), {
          type: "danger",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserLimits();
  }, []);

  const userData = user as {
    payed?: boolean;
    username?: string;
  } | null;

  const isPremium = userData?.payed || false;

  const handleUpgrade = () => {
    // Navigate to contact page for upgrade assistance with upgrade parameter
    router.push("/(drawer)/params/contact?upgrade=true");
  };

  if (loading) {
    return <Loader />;
  }

  // Render web version on web platform
  if (Platform.OS === "web") {
    return <WebLimitsScreen />;
  }

  // No need to handle error case - always show the limits data
  // Even if limits are exceeded, we still want to display the usage information

  const limitItems = [
    {
      icon: "inventory",
      title: i18n.t("productsUsage"),
      used: limits?.productCount || 0,
      limit: isPremium ? 0 : 50, // 0 means unlimited for premium
      color: "#3B82F6",
    },
    {
      icon: "account-balance-wallet",
      title: i18n.t("debtsUsage"),
      used: limits?.debtCount || 0,
      limit: isPremium ? 0 : 50, // 0 means unlimited for premium
      color: "#10B981",
    },
    {
      icon: "receipt",
      title: i18n.t("invoicesUsage"),
      used: limits?.factureCount || 0,
      limit: isPremium ? 0 : 50, // 0 means unlimited for premium
      color: "#8B5CF6",
    },
  ];

  return (
    <View style={{ flex: 1, backgroundColor: "#f5f5f5" }}>
      <StandardNavigation
        title={i18n.t("limits")}
        showBackButton={true}
        showMenuButton={true}
        showLogo={false}
      />

      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingTop: 20,
          paddingBottom: 100,
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header Section */}
        <Animated.View 
          entering={FadeInDown.duration(400).delay(100)}
          className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100"
        >
          <View
            className={`flex-row items-center justify-between ${locale === "ar" ? "flex-row-reverse" : "flex-row"}`}
          >
            <View
              className={`flex-1 ${locale === "ar" ? "items-end" : "items-start"}`}
            >
              <Text
                className={`text-2xl font-bold text-gray-800 ${locale === "ar" ? "text-right" : "text-left"}`}
              >
                {i18n.t("currentLimitsTitle")}
              </Text>
              <Text
                className={`text-gray-600 mt-2 leading-6 ${locale === "ar" ? "text-right" : "text-left"}`}
              >
                {i18n.t("limitsDescription")}
              </Text>
            </View>

            <View
              className={`w-16 h-16 rounded-full items-center justify-center ${locale === "ar" ? "mr-4" : "ml-4"}`}
              style={{ backgroundColor: isPremium ? "#10B981" : "#F59E0B" }}
            >
              <MaterialIcons
                name={isPremium ? "workspace-premium" : "free-breakfast"}
                size={24}
                color="white"
              />
            </View>
          </View>

          <View
            className={`mt-4 p-3 rounded-lg ${isPremium ? "bg-green-50 border border-green-200" : "bg-yellow-50 border border-yellow-200"}`}
          >
            <Text
              className={`font-semibold ${isPremium ? "text-green-800" : "text-yellow-800"} text-center`}
            >
              {isPremium ? i18n.t("premiumPlan") : i18n.t("freePlan")}
            </Text>
          </View>
        </Animated.View>

        {/* Usage Section */}
        <View className="mb-6">
          <Text
            className={`text-xl font-bold text-gray-800 mb-4 ${locale === "ar" ? "text-right" : "text-left"}`}
          >
            {i18n.t("usageLimits")}
          </Text>

          {limitItems.map((item, index) => (
            <LimitItem
              key={index}
              icon={item.icon}
              title={item.title}
              used={item.used}
              limit={item.limit}
              color={item.color}
              locale={locale}
            />
          ))}
        </View>

        {/* Upgrade Section - Only show for free users */}
        {!isPremium && (
          <Animated.View 
            entering={FadeInDown.duration(400).delay(400)}
            className="rounded-xl mb-6 overflow-hidden"
          >
            <LinearGradient
              colors={["#3B82F6", "#8B5CF6"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={{ padding: 24, borderRadius: 12 }}
            >
              <View
                className={`flex-row items-center ${locale === "ar" ? "flex-row-reverse" : "flex-row"}`}
              >
                <MaterialIcons name="star" size={24} color="white" />
                <Text
                  className={`text-white font-bold text-lg ${locale === "ar" ? "mr-3" : "ml-3"}`}
                >
                  {i18n.t("upgradeToGetMore")}
                </Text>
              </View>

              <Text
                className={`text-white mt-2 leading-6 ${locale === "ar" ? "text-right" : "text-left"}`}
                style={{ opacity: 0.9 }}
              >
                {i18n.t("freeTierLimitMessage")}
              </Text>

              <TouchableOpacity
                onPress={handleUpgrade}
                className="mt-4 py-3 px-6 rounded-xl"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderWidth: 1,
                  borderColor: "rgba(255, 255, 255, 0.3)",
                }}
              >
                <Text className="text-white font-semibold text-center">
                  {i18n.t("upgradeNow")}
                </Text>
              </TouchableOpacity>
            </LinearGradient>
          </Animated.View>
        )}

        {/* Premium Benefits Info */}
        {!isPremium && (
          <Animated.View 
            entering={FadeInDown.duration(400).delay(500)}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <Text
              className={`text-lg font-bold text-gray-800 mb-4 ${locale === "ar" ? "text-right" : "text-left"}`}
            >
              {i18n.locale === "ar"
                ? `${i18n.t("features")} ${i18n.t("premiumPlan")}`
                : `${i18n.t("premiumPlan")} ${i18n.t("features")}`}
            </Text>

            <View className="space-y-3">
              <View
                className={`flex-row items-center ${locale === "ar" ? "flex-row-reverse" : "flex-row"}`}
              >
                <MaterialIcons name="check-circle" size={20} color="#10B981" />
                <Text
                  className={`text-gray-700 ${locale === "ar" ? "mr-3 text-right" : "ml-3 text-left"}`}
                >
                  {i18n.t("unlimited")} {i18n.t("productsLimit")}
                </Text>
              </View>

              <View
                className={`flex-row items-center ${locale === "ar" ? "flex-row-reverse" : "flex-row"}`}
              >
                <MaterialIcons name="check-circle" size={20} color="#10B981" />
                <Text
                  className={`text-gray-700 ${locale === "ar" ? "mr-3 text-right" : "ml-3 text-left"}`}
                >
                  {i18n.t("unlimited")} {i18n.t("debtsLimit")}
                </Text>
              </View>

              <View
                className={`flex-row items-center ${locale === "ar" ? "flex-row-reverse" : "flex-row"}`}
              >
                <MaterialIcons name="check-circle" size={20} color="#10B981" />
                <Text
                  className={`text-gray-700 ${locale === "ar" ? "mr-3 text-right" : "ml-3 text-left"}`}
                >
                  {i18n.t("unlimited")} {i18n.t("invoicesLimit")}
                </Text>
              </View>
            </View>
          </Animated.View>
        )}
      </ScrollView>
    </View>
  );
};

export default LimitsPage;
